import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Link,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PlayIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Trans, useTranslation } from 'react-i18next';
import { toDisplayError } from '@utils/apiError';
import { useActiveOrgStore } from '@store/index';
import { organizationService } from '@services/organizationService';
import { contextService, type Context } from '@services/contextService';
import { testConfigService, type TestConfig } from '@services/testConfigService';
import {
  subscribeToScanProgress,
  testService,
  type RunTestResponse,
  type ScanProgressEvent,
  type ScanStatus,
  type ScanType,
} from '@services/testService';

type Phase = 'idle' | 'submitting' | 'running' | 'complete' | 'error';

// Crawl bounds mirror the API's server-side caps (REV-477). Safety limits only
// — execution is never metered.
const DEFAULT_MAX_PAGES = 50;
const DEFAULT_MAX_DEPTH = 3;
const MAX_PAGES_LIMIT = 250;
const MAX_DEPTH_LIMIT = 10;
// REV-485 crawl scope. Mirrors the API's caps in crawlLinkDiscovery.js; the
// server re-normalizes, so this is a UX convenience and not the boundary.
const MAX_SCOPE_PATTERNS = 25;

// A selectable scan target: a specific tracked place, or a per-org "one-off"
// (context-free, 24h-retention) scan. Key prefixes keep the two namespaces
// distinct in the single MUI Select.
interface Place {
  key: string;
  orgId: string;
  orgName: string;
  contextId: string | null;
  contextName: string | null;
  /** REV-939: the URL a scan of this place starts from, or null if it has none. */
  startUrl: string | null;
}

const oneOffKey = (orgId: string) => `org:${orgId}`;
const ctxKey = (contextId: string) => `ctx:${contextId}`;

function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * REV-939: the page a scan of a tracked place starts from, derived from the
 * place itself rather than from a `?url=` parameter — the link already names
 * the place, and a URL in the query string would let any link choose what gets
 * scanned under someone's tracked place.
 *
 * The API stores the identifier normalised: lowercased, without the scheme or a
 * trailing slash. So https:// goes back on. https is the safe default; a site
 * that only serves http has to be corrected by hand, which the field's helper
 * text says while the prefill is in place.
 *
 * The API does not check the identifier's shape (it trims it and bounds its
 * length), and almost anything parses as a URL once https:// is in front of
 * it — `https://intranet`, `https://ops@example.com`. So the identifier must
 * look like host[:port][/path] with a real hostname (see looksLikeHostPath)
 * before it is offered; otherwise the field is left empty.
 *
 * Only website and deployment places have one. A repository identifier
 * (github.com/owner/repo) names source code, not the site built from it, and an
 * organization place names no address at all; prefilling either would start a
 * scan of the wrong thing.
 */
function startUrlFor(
  context: Pick<Context, 'context_type' | 'canonical_identifier'>
): string | null {
  if (context.context_type !== 'website' && context.context_type !== 'deployment') return null;
  const id = (context.canonical_identifier ?? '').trim();
  if (!id || /\s/.test(id)) return null;
  const candidate = /^https?:\/\//i.test(id) ? id : `https://${id}`;
  if (!looksLikeHostPath(candidate.replace(/^https?:\/\//i, ''))) return null;
  return isHttpUrl(candidate) ? candidate : null;
}

/** One DNS label: letters, digits and inner hyphens, at most 63 characters. */
const HOST_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

/**
 * REV-939: true when `value` is host[:port][/path] and the host is a real
 * hostname — `localhost`, or two or more DNS labels (which covers dotted IPv4
 * too). Rejects what the URL parser would otherwise accept: a bare word
 * (`intranet`), userinfo (`ops@example.com`), underscores, empty labels.
 */
function looksLikeHostPath(value: string): boolean {
  const match = /^([^/?#]+)([/?#].*)?$/.exec(value);
  if (!match) return false;
  const [, hostPort] = match;
  const hostMatch = /^([^:]+)(?::(\d{1,5}))?$/.exec(hostPort ?? '');
  if (!hostMatch) return false;
  const host = hostMatch[1] ?? '';
  if (host.toLowerCase() === 'localhost') return true;
  const labels = host.split('.');
  return labels.length >= 2 && labels.every((label) => HOST_LABEL.test(label));
}

/**
 * Parse a text input to an integer clamped to [min, max]; falls back to
 * `fallback` for empty/non-numeric input. The server re-clamps, so this is a
 * UX convenience, not the security boundary.
 */
function clampInt(value: string, min: number, max: number, fallback: number): number {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(Math.max(n, min), max);
}

/**
 * REV-485: parse a one-pattern-per-line textarea into a clean pattern list.
 * Blank lines and stray whitespace are dropped rather than rejected — a
 * trailing newline is a typo, not an instruction. Returns undefined when
 * nothing usable was entered so the key is omitted from the request entirely:
 * an empty array would state a filter the user never asked for.
 */
function splitPatterns(value: string): string[] | undefined {
  const patterns = value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, MAX_SCOPE_PATTERNS);
  return patterns.length > 0 ? patterns : undefined;
}

/**
 * REV-131 / REV-463: ad-hoc test execution. The scan target is chosen as a
 * single "Tracked Place" (organization–context combination, since a user may
 * belong to several orgs). Once a place is chosen, the scan inherits that
 * place's effective configuration (WCAG version/level, viewport, …) resolved
 * org → context (REV-460) — so there is no separate Ruleset field here. Submits
 * to POST /v1/tests and streams progress over SSE.
 */
export default function TestExecutionPage() {
  const { t } = useTranslation();
  const { activeOrg } = useActiveOrgStore();
  const [searchParams] = useSearchParams();
  const urlContextId = searchParams.get('contextId');

  const [url, setUrl] = useState('');
  const [captureScreenshot, setCaptureScreenshot] = useState(true);

  // REV-477: single page (adhoc) vs whole-site (crawl).
  const [scanMode, setScanMode] = useState<ScanType>('adhoc');
  const [maxPages, setMaxPages] = useState<string>(String(DEFAULT_MAX_PAGES));
  const [maxDepth, setMaxDepth] = useState<string>(String(DEFAULT_MAX_DEPTH));
  // REV-485: crawl scope — which pages the crawl may reach, as opposed to the
  // bounds above, which only say how many.
  const [includePatterns, setIncludePatterns] = useState('');
  const [excludePatterns, setExcludePatterns] = useState('');
  const [includeSubdomains, setIncludeSubdomains] = useState(false);

  const [places, setPlaces] = useState<Place[]>([]);
  const [loadingPlaces, setLoadingPlaces] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string>('');
  // REV-495: when the page is reached from a tracked place's "Run test"
  // (/tests/new?contextId=…), pre-select that place and lock the picker so the
  // scan runs against the context the user came from.
  const [lockedFromUrl, setLockedFromUrl] = useState(false);
  const [effective, setEffective] = useState<TestConfig | null>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [run, setRun] = useState<RunTestResponse | null>(null);
  const [runOrgId, setRunOrgId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ScanProgressEvent | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * REV-707: pressing Run test with an invalid form left focus on the BUTTON,
   * and the message was rendered as plain helper text with no live region —
   * measured: focus=BUTTON, helperRole=NONE, role="alert" nodes = 0. A screen
   * reader user got nothing at all: no announcement, no focus move, and no
   * indication why the button appeared to do nothing.
   *
   * Focus goes to the thing the user has to change (WCAG 3.3.1), which for a
   * field-level problem is the field, and for a form-level one is the message.
   */
  const urlInputRef = useRef<HTMLInputElement>(null);
  const formErrorRef = useRef<HTMLDivElement>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  const selected = useMemo(
    () => places.find((p) => p.key === selectedKey) ?? null,
    [places, selectedKey]
  );

  // Build the Tracked Place options across every org the user belongs to: one
  // "(no tracked place)" entry per org plus one per context.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadingPlaces(true);
      try {
        const orgPage = await organizationService.list({ limit: 100 });
        const orgs = orgPage.items;
        const lists = await Promise.all(
          orgs.map((o) =>
            contextService
              .list(o.id, { limit: 100 })
              .then((r) => r.items.filter((c) => c.status !== 'removed'))
              .catch(() => [])
          )
        );
        if (cancelled) return;
        const next: Place[] = [];
        orgs.forEach((o, i) => {
          next.push({
            key: oneOffKey(o.id),
            orgId: o.id,
            orgName: o.name,
            contextId: null,
            contextName: null,
            startUrl: null,
          });
          lists[i]?.forEach((c) => {
            next.push({
              key: ctxKey(c.id),
              orgId: o.id,
              orgName: o.name,
              contextId: c.id,
              contextName: c.name,
              startUrl: startUrlFor(c),
            });
          });
        });
        setPlaces(next);
        // REV-495: if we arrived from a tracked place (?contextId=…) and that
        // place exists, pre-select and lock it. Otherwise default to the active
        // org's one-off entry, else the first option.
        const fromUrl =
          urlContextId && next.some((p) => p.key === ctxKey(urlContextId))
            ? ctxKey(urlContextId)
            : null;
        // Lock ONLY while the URL context resolves to a real place. This effect
        // re-runs when activeOrg changes; if the context is no longer accessible
        // (removed, or the user switched orgs), release the lock rather than
        // stranding the user on a disabled picker (REV-495 review).
        setLockedFromUrl(!!fromUrl);
        const preferred = fromUrl ?? (activeOrg ? oneOffKey(activeOrg.id) : next[0]?.key);
        // REV-939: a place reached from its own "Run a test" brings its address
        // with it. Never over a URL the user has already typed.
        const fromUrlPlace = fromUrl ? next.find((p) => p.key === fromUrl) : undefined;
        if (fromUrlPlace?.startUrl) {
          const startUrl = fromUrlPlace.startUrl;
          setUrl((cur) => cur || startUrl);
        }
        setSelectedKey((cur) => {
          if (fromUrl) return fromUrl;
          // Keep the current selection only if it still resolves to a real
          // place; otherwise fall back to the default so a stale key can't
          // leave `selected` null with the control disabled.
          if (cur && next.some((p) => p.key === cur)) return cur;
          return preferred || '';
        });
      } catch {
        if (!cancelled)
          setError(t('testRun.errors.loadPlaces', 'Could not load your tracked places.'));
      } finally {
        if (!cancelled) setLoadingPlaces(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrg, t, urlContextId]);

  // When a real tracked place is selected, fetch its effective (inherited)
  // config to show what the scan will use. One-off scans have no context, so
  // they use the platform/org defaults and show no inherited summary.
  useEffect(() => {
    if (!selected || !selected.contextId) {
      setEffective(null);
      return;
    }
    let cancelled = false;
    testConfigService
      .getEffectiveConfig(selected.orgId, selected.contextId)
      .then((cfg) => {
        if (!cancelled) setEffective(cfg);
      })
      .catch(() => {
        if (!cancelled) setEffective(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  // SSE subscription for the active run.
  useEffect(() => {
    if (phase !== 'running' || !run) return;
    const unsub = subscribeToScanProgress(
      run.scanRunId,
      (evt) => {
        setProgress((prev) => ({ ...(prev || {}), ...evt }));
        if (evt.status === 'completed') {
          setPhase('complete');
        } else if (evt.status === 'failed' || evt.status === 'cancelled') {
          setPhase('error');
          setError(
            evt.errorMessage ||
              (evt.status === 'cancelled'
                ? t('testRun.cancelled', 'Scan was cancelled.')
                : t('testRun.failed', 'Scan failed.'))
          );
        }
      },
      () => {
        void pollStatus();
      }
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, run?.scanRunId]);

  const pollStatus = useCallback(async () => {
    if (!run || !runOrgId) return;
    try {
      const { scanRun } = await testService.getTest(run.scanRunId, runOrgId);
      if (scanRun.status === 'completed') {
        setPhase('complete');
      } else if (scanRun.status === 'failed' || scanRun.status === 'cancelled') {
        setPhase('error');
        setError(
          scanRun.errorMessage ||
            (scanRun.status === 'cancelled'
              ? t('testRun.cancelled', 'Scan was cancelled.')
              : t('testRun.failed', 'Scan failed.'))
        );
      } else {
        setProgress((prev) => ({
          ...(prev || {}),
          status: scanRun.status,
          totalTargets: scanRun.totalTargets,
          completedTargets: scanRun.completedTargets,
        }));
      }
    } catch {
      /* will retry on next SSE event */
    }
  }, [run, runOrgId, t]);

  const handleRun = async () => {
    if (!selected) {
      setError(t('testRun.pickPlace', 'Choose a tracked place to scan.'));
      // REV-707: the alert is rendered by this same state update, so wait a
      // tick for it to exist before trying to focus it.
      requestAnimationFrame(() => formErrorRef.current?.focus());
      return;
    }
    if (!isHttpUrl(url.trim())) {
      setUrlError(t('testRun.urlInvalid', 'Enter a valid http(s) URL.'));
      urlInputRef.current?.focus();
      return;
    }
    // A whole-site crawl is anchored to a tracked place (context): the
    // discovered pages become that context's targets. Guard here so the user
    // gets a clear message instead of a 400 from the API.
    if (scanMode === 'crawl' && !selected.contextId) {
      setError(
        t(
          'testRun.crawlNeedsContext',
          'Whole-site crawl needs a tracked place. Pick a context (not a one-off) to crawl its site.'
        )
      );
      requestAnimationFrame(() => formErrorRef.current?.focus());
      return;
    }
    setUrlError(null);
    setPhase('submitting');
    setError(null);
    setProgress(null);
    try {
      // REV-485: scope keys are spread conditionally so an untouched field
      // sends nothing at all rather than an empty array (which would read as a
      // filter the user never set).
      const include = splitPatterns(includePatterns);
      const exclude = splitPatterns(excludePatterns);
      const crawlSettings =
        scanMode === 'crawl'
          ? {
              maxPages: clampInt(maxPages, 1, MAX_PAGES_LIMIT, DEFAULT_MAX_PAGES),
              maxDepth: clampInt(maxDepth, 0, MAX_DEPTH_LIMIT, DEFAULT_MAX_DEPTH),
              ...(include ? { includePatterns: include } : {}),
              ...(exclude ? { excludePatterns: exclude } : {}),
              ...(includeSubdomains ? { includeSubdomains: true } : {}),
            }
          : {};
      const res = await testService.runTest({
        url: url.trim(),
        organizationId: selected.orgId,
        contextId: selected.contextId ?? undefined,
        scanType: scanMode,
        // WCAG ruleset + viewport are inherited from the tracked place's
        // effective config (REV-460); only the per-run screenshot toggle and
        // (for crawls) the page/depth bounds are sent as overrides.
        settings: { captureScreenshot, ...crawlSettings },
      });
      setRun(res);
      setRunOrgId(selected.orgId);
      setPhase('running');
    } catch (err) {
      setPhase('error');
      setError(toDisplayError(err, t('testRun.errors.submit', 'Failed to submit test')));
    }
  };

  const handleCancel = async () => {
    if (!run || !runOrgId) return;
    try {
      await testService.cancel(run.scanRunId, runOrgId);
      setPhase('error');
      setError(t('testRun.cancelled', 'Scan was cancelled.'));
    } catch (err) {
      setError(toDisplayError(err, t('testRun.errors.cancel', 'Failed to cancel scan')));
    }
  };

  const progressValue = useMemo(() => {
    const pv = progress?.progress;
    if (typeof pv === 'number' && pv >= 0 && pv <= 100) return pv;
    const total = progress?.totalTargets;
    const done = progress?.completedTargets;
    if (typeof total === 'number' && typeof done === 'number' && total > 0) {
      return Math.min(100, Math.round((done / total) * 100));
    }
    return undefined;
  }, [progress]);

  const statusLabel = (s?: ScanStatus): string => {
    if (!s) return t('testRun.statuses.pending', 'Pending');
    return t(`testRun.statuses.${s}`, s);
  };

  // REV-732: a destination, not a handler. The block that renders this only
  // exists when `run` does, so the empty fallback is unreachable — but it is
  // an empty href rather than a broken one if that guard ever moves.
  const viewResultsHref = run ? `/tests/${run.scanRunId}` : '';

  const resetToIdle = () => {
    setPhase('idle');
    setRun(null);
    setRunOrgId(null);
    setProgress(null);
    setError(null);
  };

  const inheritedSummary = useMemo(() => {
    if (!effective) return null;
    const wcag = effective.wcag;
    const vp = effective.runtime?.viewport;
    const parts: string[] = [];
    if (wcag?.version && wcag?.level) parts.push(`WCAG ${wcag.version} ${wcag.level}`);
    if (vp?.width && vp?.height) parts.push(`${vp.width}×${vp.height}`);
    if (wcag?.excludedCriteria && wcag.excludedCriteria.length > 0) {
      parts.push(
        t('testRun.excludedCount', {
          defaultValue: '{{count}} excluded SC',
          count: wcag.excludedCriteria.length,
        })
      );
    }
    return parts.join(' · ');
  }, [effective, t]);

  if (loadingPlaces && places.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress aria-label={t('common.loading', 'Loading...')} />
      </Box>
    );
  }

  if (!loadingPlaces && places.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          {/* REV-682: "Create an organization" was plain text telling the user
              to do something, with no way to do it. It is the only action
              available on this screen, so it is a link — and by the rule in
              CLAUDE.md, a link rather than a button because it goes to a URL.

              `?create=1` opens the create dialog on arrival, the same entry
              point the org switcher uses for a user with no organizations
              (REV-676), so both routes into this state behave alike.

              <Trans> keeps the sentence one translatable unit: splitting it
              into "Create an organization" + " before running a test…" would
              hard-code English word order. */}
          <Trans
            i18nKey="testRun.noOrgs"
            defaults="<0>Create an organization</0> before running a test. Results need to be scoped to an org for storage and billing."
            components={[<Link component={RouterLink} to="/orgs?create=1" key="create" />]}
          />
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
        {selected?.contextName
          ? t('testRun.titleWithPlace', {
              defaultValue: 'Run a test — {{place}}',
              place: selected.contextName,
            })
          : t('testRun.title', 'Run a test')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'testRun.subtitle',
          'Enter a URL and submit an on-demand accessibility scan. Results stream in real time.'
        )}
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
          // REV-707: focusable so validation can send the user here, and so a
          // screen reader reads the message rather than announcing a button
          // that appeared to do nothing. tabIndex -1 keeps it out of the tab
          // order — it is a focus TARGET, not a stop on the way through.
          ref={formErrorRef}
          tabIndex={-1}
        >
          {error}
        </Alert>
      )}
      {run?.over_limit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t(
            'testRun.overLimit',
            'Your organization is over its plan context limit. This scan ran, but results will only be retained for 7 days. Upgrade or trim contexts to restore full retention.'
          )}
        </Alert>
      )}

      {(phase === 'idle' || phase === 'submitting' || phase === 'error') && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <TextField
              label={t('testRun.urlLabel', 'URL to test')}
              placeholder={t('testRun.urlPlaceholder', 'https://example.com/page')}
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (urlError) setUrlError(null);
              }}
              error={!!urlError}
              inputRef={urlInputRef}
              helperText={
                urlError ||
                // REV-939: while the URL is still the one prefilled from the
                // place, say so — and that https:// was assumed, since the
                // stored identifier has no scheme and an http-only site
                // (an intranet, say) needs it changed by hand.
                (selected?.startUrl && url === selected.startUrl && selected.contextName
                  ? t('testRun.urlPrefilled', {
                      defaultValue:
                        'Prefilled from {{place}}. If the site only serves http://, change the scheme.',
                      place: selected.contextName,
                    })
                  : t('testRun.urlHelp', 'http:// or https:// — a fully-qualified URL.'))
              }
              // REV-707: the message has to be ANNOUNCED, not just displayed.
              // Only when there is an error — a permanently-live hint would
              // interrupt the user every time the help text re-renders.
              FormHelperTextProps={urlError ? { role: 'alert' } : undefined}
              fullWidth
              // autoFocus is correct on the single primary input of the page.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />

            <FormControl>
              <FormLabel id="testrun-scanmode-label">
                {t('testRun.scanModeLabel', 'What to scan')}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="testrun-scanmode-label"
                name="scan-mode"
                value={scanMode}
                onChange={(e) => setScanMode(e.target.value as ScanType)}
              >
                <FormControlLabel
                  value="adhoc"
                  control={<Radio />}
                  label={t('testRun.scanModeSingle', 'Single page')}
                />
                <FormControlLabel
                  value="crawl"
                  control={<Radio />}
                  label={t('testRun.scanModeCrawl', 'Whole site')}
                />
              </RadioGroup>
              <FormHelperText>
                {scanMode === 'crawl'
                  ? t(
                      'testRun.scanModeCrawlHelp',
                      'Starts at the URL above and follows same-site links, scanning each page it finds (bounded by the limits below).'
                    )
                  : t('testRun.scanModeSingleHelp', 'Scans only the URL you entered.')}
              </FormHelperText>
            </FormControl>

            {scanMode === 'crawl' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('testRun.maxPagesLabel', 'Max pages')}
                  type="number"
                  value={maxPages}
                  onChange={(e) => setMaxPages(e.target.value)}
                  inputProps={{
                    min: 1,
                    max: MAX_PAGES_LIMIT,
                    'aria-describedby': 'testrun-maxpages-help',
                  }}
                  helperText={
                    <span id="testrun-maxpages-help">
                      {t('testRun.maxPagesHelp', {
                        defaultValue: '1–{{max}} pages. Default {{def}}.',
                        max: MAX_PAGES_LIMIT,
                        def: DEFAULT_MAX_PAGES,
                      })}
                    </span>
                  }
                  fullWidth
                />
                <TextField
                  label={t('testRun.maxDepthLabel', 'Max link depth')}
                  type="number"
                  value={maxDepth}
                  onChange={(e) => setMaxDepth(e.target.value)}
                  inputProps={{
                    min: 0,
                    max: MAX_DEPTH_LIMIT,
                    'aria-describedby': 'testrun-maxdepth-help',
                  }}
                  helperText={
                    <span id="testrun-maxdepth-help">
                      {t('testRun.maxDepthHelp', {
                        defaultValue: '0–{{max}} clicks from the start page. Default {{def}}.',
                        max: MAX_DEPTH_LIMIT,
                        def: DEFAULT_MAX_DEPTH,
                      })}
                    </span>
                  }
                  fullWidth
                />
              </Stack>
            )}

            {/* REV-485: crawl scope. The bounds above say how MUCH to crawl;
                these say WHICH pages qualify. */}
            {scanMode === 'crawl' && (
              <Stack spacing={2}>
                <TextField
                  label={t('testRun.includePatternsLabel', 'Only crawl URLs matching (optional)')}
                  value={includePatterns}
                  onChange={(e) => setIncludePatterns(e.target.value)}
                  multiline
                  minRows={2}
                  inputProps={{ 'aria-describedby': 'testrun-include-help' }}
                  helperText={
                    <span id="testrun-include-help">
                      {t('testRun.includePatternsHelp', {
                        defaultValue:
                          'One pattern per line, up to {{max}}. Use * as a wildcard, e.g. */docs/*. Leave blank to crawl everything on the site.',
                        max: MAX_SCOPE_PATTERNS,
                      })}
                    </span>
                  }
                  fullWidth
                />
                <TextField
                  label={t('testRun.excludePatternsLabel', 'Never crawl URLs matching (optional)')}
                  value={excludePatterns}
                  onChange={(e) => setExcludePatterns(e.target.value)}
                  multiline
                  minRows={2}
                  inputProps={{ 'aria-describedby': 'testrun-exclude-help' }}
                  helperText={
                    <span id="testrun-exclude-help">
                      {t('testRun.excludePatternsHelp', {
                        defaultValue:
                          'One pattern per line, up to {{max}}. These win over the list above, so you can allow */docs/* and still skip */docs/private/*.',
                        max: MAX_SCOPE_PATTERNS,
                      })}
                    </span>
                  }
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeSubdomains}
                      onChange={(e) => setIncludeSubdomains(e.target.checked)}
                      inputProps={{ 'aria-describedby': 'testrun-subdomains-help' }}
                    />
                  }
                  label={t('testRun.includeSubdomainsLabel', 'Also crawl subdomains')}
                />
                <FormHelperText id="testrun-subdomains-help" sx={{ mt: -1 }}>
                  {t(
                    'testRun.includeSubdomainsHelp',
                    'Follow links to hosts under the start URL, e.g. docs.example.com when starting at example.com.'
                  )}
                </FormHelperText>
              </Stack>
            )}

            <FormControl fullWidth>
              <InputLabel id="testrun-place-label">
                {t('testRun.placeLabel', 'Tracked place')}
              </InputLabel>
              <Select
                labelId="testrun-place-label"
                id="testrun-place"
                value={selected ? selectedKey : ''}
                label={t('testRun.placeLabel', 'Tracked place')}
                onChange={(e: SelectChangeEvent) => setSelectedKey(e.target.value)}
                // REV-495: locked when we arrived from a specific tracked place.
                disabled={lockedFromUrl}
              >
                {places.map((p) => (
                  <MenuItem key={p.key} value={p.key}>
                    {p.contextName
                      ? `${p.orgName} — ${p.contextName}`
                      : t('testRun.oneOff', {
                          defaultValue: '{{org}} — (one-off, no tracked place)',
                          org: p.orgName,
                        })}
                  </MenuItem>
                ))}
              </Select>
              {/* aria-live: the inherited-settings summary updates asynchronously
                  after a place is chosen, so announce the change to AT. */}
              <FormHelperText aria-live="polite">
                {selected && !selected.contextId
                  ? t(
                      'testRun.oneOffHelp',
                      'One-off scans use your organization defaults and are retained for 24 hours.'
                    )
                  : inheritedSummary
                    ? t('testRun.inherited', {
                        defaultValue: 'Inherited settings: {{summary}}',
                        summary: inheritedSummary,
                      })
                    : t(
                        'testRun.placeHelp',
                        'The scan inherits this place’s configured WCAG ruleset and options.'
                      )}
              </FormHelperText>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={captureScreenshot}
                  onChange={(e) => setCaptureScreenshot(e.target.checked)}
                />
              }
              label={t('testRun.screenshot', 'Capture screenshot for each finding')}
            />

            <Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayIcon />}
                onClick={handleRun}
                // Never pre-disable submit (mandated form UX): keep it enabled
                // and validate on click; only disable while the request is
                // in flight.
                disabled={phase === 'submitting'}
              >
                {phase === 'submitting'
                  ? t('testRun.submitting', 'Submitting…')
                  : scanMode === 'crawl'
                    ? t('testRun.runCrawl', 'Crawl site')
                    : t('testRun.run', 'Run test')}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {phase === 'running' && run && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} aria-label={t('testRun.running', 'Scanning')} />
              <Typography variant="h6">
                {t('testRun.scanning', 'Scanning')} {run.url}
              </Typography>
            </Box>
            <Chip size="small" label={statusLabel(progress?.status ?? run.status)} />
            {typeof progressValue === 'number' ? (
              <Box>
                <LinearProgress variant="determinate" value={progressValue} />
                <Typography variant="caption" color="text.secondary">
                  {progressValue}%{' '}
                  {progress?.completedTargets != null && progress?.totalTargets != null
                    ? ` — ${progress.completedTargets}/${progress.totalTargets}`
                    : ''}
                </Typography>
              </Box>
            ) : (
              <LinearProgress />
            )}
            {progress?.message && (
              <Typography variant="body2" color="text.secondary">
                {progress.message}
              </Typography>
            )}
            <Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                {t('testRun.cancel', 'Cancel scan')}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {phase === 'complete' && run && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Alert severity="success">{t('testRun.complete', 'Scan complete.')}</Alert>
            <Typography variant="body2" color="text.secondary">
              {run.url}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                endIcon={<OpenInNewIcon />}
                component={RouterLink}
                to={viewResultsHref}
              >
                {t('testRun.viewResults', 'View results')}
              </Button>
              <Button variant="text" onClick={resetToIdle}>
                {t('testRun.runAnother', 'Run another')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
